import { toast } from '@/lib/toast';
import { useUpdateVariantFields } from '@/hooks/useProduct';
import { InlineEditCell, type InlineEditKind } from './InlineEditCell';

type VariantField = 'sku' | 'barcode' | 'price' | 'compare_at_price' | 'option1';

const FIELD_META: Record<
  VariantField,
  { kind: InlineEditKind; label: string; empty: string }
> = {
  sku: { kind: 'text', label: 'SKU', empty: 'Set SKU' },
  barcode: { kind: 'text', label: 'Barcode', empty: 'Set barcode' },
  price: { kind: 'currency', label: 'Online price', empty: 'Set price' },
  compare_at_price: { kind: 'currency', label: 'Compare-at price', empty: 'Set price' },
  option1: { kind: 'text', label: 'Size', empty: 'Set size' },
};

interface VariantFieldCellProps {
  shopifyVariantId: number | null;
  shopifyProductId: number;
  field: VariantField;
  value: string | number | null;
  className?: string;
}

/**
 * Click-to-edit cell for a single variant field (size/option1, SKU, barcode,
 * online price, compare-at price). Self-contained: a save writes the variant
 * locally and pushes to Shopify via the shared variant-edit path. Used on the
 * Variants list, the expanded rows on the Products list, and the product
 * detail page.
 */
export function VariantFieldCell({
  shopifyVariantId,
  shopifyProductId,
  field,
  value,
  className,
}: VariantFieldCellProps) {
  const update = useUpdateVariantFields();
  const meta = FIELD_META[field];

  const commit = (next: string | number | null) => {
    if (shopifyVariantId == null) {
      toast.error('No variant to edit');
      return;
    }
    update.mutate(
      { shopifyVariantId, shopifyProductId, fields: { [field]: next } },
      {
        onSuccess: () => toast.success(`${meta.label} saved to Shopify`),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  return (
    <InlineEditCell
      value={value}
      kind={meta.kind}
      onCommit={commit}
      pending={update.isPending}
      emptyLabel={meta.empty}
      className={className}
    />
  );
}
